import { useCCFUpsellDialog } from '@app/hooks/use-ccf-upsell-dialog';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { cn } from '@shared/lib/utils';
import { ReactNode } from 'react';

const DEFAULT_ICON = <Icons.carrot className="size-5 text-orange-400" />;
const DEFAULT_TITLE = 'Add Scope 3 to your company reports';
const DEFAULT_BODY = (
  <>
    <Text className="mb-2 text-wrap font-normal">
      On average, emissions from purchased food and drink account for over 70%
      of total company emissions for food businesses like yours.
    </Text>
    <Text className="text-wrap font-normal">
      Book a call with our team to unlock your Scope 3 emissions today.
    </Text>
  </>
);

export function CCFAddScope3TooltipContent({
  showDialog: externalShowDialog,
  className,
  icon = DEFAULT_ICON,
  title = DEFAULT_TITLE,
  body = DEFAULT_BODY,
}: {
  showDialog?: () => void;
  className?: string;
  icon?: ReactNode;
  title?: ReactNode;
  body?: ReactNode;
}) {
  const { showDialog: internalShowDialog, UpsellDialogComponent } =
    useCCFUpsellDialog();
  const showDialog = externalShowDialog || internalShowDialog;

  return (
    <>
      {!externalShowDialog && UpsellDialogComponent}
      <div className={cn('max-w-sm flex-col space-y-3 p-2 text-sm', className)}>
        <div className="flex items-center space-x-2">
          {icon}
          <Text variant="cardTitle">{title}</Text>
        </div>
        <div>{body}</div>
        <Button onClick={showDialog} className="bg-teal">
          Book a call
        </Button>
      </div>
    </>
  );
}

export function CCFCompanyReportsUpsellTooltipContent({
  showDialog,
}: {
  showDialog?: () => void;
}) {
  return (
    <CCFAddScope3TooltipContent
      showDialog={showDialog}
      icon={<Icons.clipboardList className="size-5 text-orange-400" />}
      title={
        <span className="whitespace-nowrap">Company Carbon Footprint</span>
      }
      body={
        <>
          <Text className="mb-3 text-wrap font-normal">
            Measure your Scope 1, 2 and 3 emissions across your business and
            generate company carbon footprint reports.
          </Text>
          <div className="mb-3 rounded-md bg-teal-50 p-2.5">
            <div className="mb-1 flex items-center gap-2">
              <Text variant="cardTitle" className="text-sm">
                Now includes SECR reporting
              </Text>
            </div>
            <Text className="text-wrap text-xs" variant="subtle">
              Generate Streamlined Energy and Carbon Reporting (SECR)-compliant
              disclosures from your company report.
            </Text>
          </div>
          <Text className="text-wrap font-normal">
            Book a call with our team to unlock company reports today.
          </Text>
        </>
      }
    />
  );
}

export default function CCFAddScope3Tooltip({
  trigger,
  className,
}: {
  trigger: React.ReactNode;
  className?: string;
}) {
  const { showDialog, UpsellDialogComponent } = useCCFUpsellDialog();
  return (
    <>
      {UpsellDialogComponent}
      <SimpleTooltip
        delayDuration={200}
        trigger={trigger}
        content={
          <CCFAddScope3TooltipContent
            showDialog={showDialog}
            className={className}
          />
        }
      />
    </>
  );
}
